import { useEffect, useRef, useCallback } from 'react';
import type { FieldErrors, FieldValues, UseFormGetValues } from 'react-hook-form';
import { trackEvent, tagClaritySession } from '@/shared/analytics';
import { toSnakeCase } from '@/shared/utils/string';

interface UseFormTrackingOptions<T extends FieldValues> {
  formType: string;
  clarityStage?: string;
  clarityStartedStage?: string;
  startEvent?: string;
  getValues: UseFormGetValues<T>;
  errors: FieldErrors<T>;
  properties?: Record<string, unknown>;
}

type ErrorTrigger = 'field' | 'submit';

const NON_FIELD_ERROR_KEY = 'root';

function errorTypesByField<T extends FieldValues>(fieldErrors: FieldErrors<T>): Record<string, string> {
  const types: Record<string, string> = {};

  for (const [field, error] of Object.entries(fieldErrors)) {
    if (!error || field === NON_FIELD_ERROR_KEY) continue;

    types[field] = (error as { type?: string }).type ?? 'unknown';
  }

  return types;
}

/**
 * Tracks form field interactions, validation errors, and abandonment
 * using event delegation on the form element — no per-field props needed.
 *
 * Usage:
 *   const { formProps, wrapSubmit, onInvalid } = useFormTracking({ ... });
 *   <form {...formProps} onSubmit={handleSubmit(wrapSubmit(onSubmit), onInvalid)}>
 */
export function useFormTracking<T extends FieldValues>({
  formType,
  clarityStage,
  clarityStartedStage,
  startEvent,
  getValues,
  errors,
  properties,
}: UseFormTrackingOptions<T>) {
  const hasTrackedStart = useRef(false);
  const reportedErrors = useRef<Record<string, string>>({});
  const formSubmitted = useRef(false);
  const fieldFocusTimes = useRef<Record<string, number>>({});
  const lastFieldFocused = useRef<string | null>(null);
  const formMountTime = useRef(0);
  const propertiesRef = useRef(properties);
  useEffect(() => {
    propertiesRef.current = properties;
  });

  // Clarity tag + abandonment listener
  useEffect(() => {
    formMountTime.current = Date.now();
    if (clarityStage) tagClaritySession('funnel_stage', clarityStage);

    const handleBeforeUnload = () => {
      if (!formSubmitted.current && hasTrackedStart.current) {
        const values = getValues();
        const fieldsFilled = Object.entries(values).filter(
          ([, v]) => (typeof v === 'boolean' ? v : !!v),
        ).length;
        trackEvent('form_abandon', {
          fields_filled: fieldsFilled,
          last_field_focused: lastFieldFocused.current,
          time_on_form_ms: Date.now() - formMountTime.current,
          form_type: formType,
          ...propertiesRef.current,
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleBeforeUnload();
    };
  }, [clarityStage, formType, getValues]);

  // Delegated focus handler — attached to <form>
  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLFormElement>) => {
      // Event delegation: target is the focused input, not the form
      const name = (e.target as unknown as HTMLInputElement).name;
      if (!name) return;

      fieldFocusTimes.current[name] = Date.now();
      lastFieldFocused.current = name;

      if (!hasTrackedStart.current) {
        hasTrackedStart.current = true;
        if (startEvent) trackEvent(startEvent, { form_type: formType, ...propertiesRef.current });
        if (clarityStartedStage) tagClaritySession('funnel_stage', clarityStartedStage);
      }
    },
    [clarityStartedStage, startEvent, formType],
  );

  // Delegated blur handler — attached to <form>
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLFormElement>) => {
      // Event delegation: target is the focused input, not the form
      const target = e.target as unknown as HTMLInputElement;
      const name = target.name;
      if (!name) return;

      const focusTime = fieldFocusTimes.current[name];
      const duration = focusTime ? Date.now() - focusTime : 0;

      trackEvent('form_field_interaction', {
        field_name: toSnakeCase(name),
        duration_ms: duration,
        had_value: target.type === 'checkbox' ? target.checked : !!target.value,
        form_type: formType,
      });
    },
    [formType],
  );

  const reportError = useCallback(
    (field: string, errorType: string, trigger: ErrorTrigger) => {
      trackEvent('form_validation_error', {
        field_name: toSnakeCase(field),
        error_type: errorType,
        trigger,
        form_type: formType,
      });
    },
    [formType],
  );

  const onInvalid = useCallback(
    (fieldErrors: FieldErrors<T>) => {
      const blocking = errorTypesByField(fieldErrors);
      reportedErrors.current = { ...reportedErrors.current, ...blocking };

      for (const [field, errorType] of Object.entries(blocking)) {
        reportError(field, errorType, 'submit');
      }
    },
    [reportError],
  );

  useEffect(() => {
    const alreadyReported = reportedErrors.current;
    const onScreen = errorTypesByField(errors);
    reportedErrors.current = onScreen;

    for (const [field, errorType] of Object.entries(onScreen)) {
      if (alreadyReported[field] !== errorType) reportError(field, errorType, 'field');
    }
  });

  const wrapSubmit = useCallback(
    (handler: (values: T) => void) => {
      return (values: T) => {
        formSubmitted.current = true;
        handler(values);
      };
    },
    [],
  );

  const formProps = { onFocusCapture: handleFocus, onBlurCapture: handleBlur };

  return { formProps, wrapSubmit, onInvalid };
}
